import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { isGlobalAdmin } from '@/lib/workspaces';
import { fmtDate as fmtMMDDYY } from '@/lib/dates';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Wrench, Search, Sparkles, Plus, Link2, AlertTriangle } from 'lucide-react';
import { useOpsPurgeAccess, PurgeButton, PurgeReviewDialog } from '@/components/operations/hub/OpsPurge';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const fmtDate = (d) => fmtMMDDYY(d) || '—';

const STATUS_STYLE = {
  Open: 'bg-amber-100 text-amber-700',
  'In Progress': 'bg-blue-100 text-blue-700',
  Verified: 'bg-[#8FC93A]/20 text-[#4d7c0f]',
  Completed: 'bg-[#8FC93A]/20 text-[#4d7c0f]',
  Cancelled: 'bg-slate-100 text-slate-500',
};
const PRIORITY_STYLE = {
  Critical: 'bg-red-100 text-red-700',
  High: 'bg-orange-100 text-orange-700',
  Medium: 'bg-slate-100 text-slate-600',
  Low: 'bg-slate-100 text-slate-500',
};
const STATUSES = ['Open', 'In Progress', 'Verified', 'Completed', 'Cancelled'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const OperationsCorrectiveActions = ({ initialFilter }) => {
  const { token, user } = useContext(AuthContext);
  const admin = isGlobalAdmin(user) || ['owner', 'admin', 'manager'].includes(user?.role);
  const canPurge = useOpsPurgeAccess();
  const [rows, setRows] = useState(null);
  const [statusFilter, setStatusFilter] = useState(initialFilter === 'open' ? 'open' : 'all');
  const [overdue, setOverdue] = useState(initialFilter === 'overdue');
  const [followupDue, setFollowupDue] = useState(initialFilter === 'followup_due');
  const [needsAttention, setNeedsAttention] = useState(initialFilter === 'needs_attention');
  const [demoMode, setDemoMode] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showPurge, setShowPurge] = useState(false);
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const load = () => {
    const params = new URLSearchParams();
    if (demoMode) params.set('demo_only', 'true');
    if (followupDue) params.set('followup_due', 'true');
    if (needsAttention) params.set('needs_attention', 'true');
    axios.get(`${API}/operations/corrective-actions?${params.toString()}`, auth(token))
      .then((r) => { setRows(r.data); setSelected([]); })
      .catch(() => setErr('Unable to load corrective actions.'));
  };
  useEffect(() => { load(); }, [token, demoMode, followupDue, needsAttention]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) => {
      if (statusFilter === 'open' && !['Open', 'In Progress'].includes(r.status)) return false;
      if (!['all', 'open'].includes(statusFilter) && r.status !== statusFilter) return false;
      if (overdue && !r.is_overdue) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!`${r.action_number} ${r.legacy_number || ''} ${r.summary} ${r.responsible_person || ''} ${r.location_name || ''}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, overdue, search, followupDue]);

  const seed = async () => {
    try {
      const r = await axios.post(`${API}/operations/corrective-actions/seed`, {}, auth(token));
      toast.success(`Seed complete — ${r.data.created} added, ${r.data.skipped_existing} already present.`);
      load();
    } catch { toast.error('Seed failed.'); }
  };

  return (
    <div data-testid="ops-corrective-actions">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">Operations Module</div>
          <h1 className="mt-1.5 text-[30px] leading-tight font-extrabold tracking-tight text-[#0E2A47]">Corrective Actions</h1>
          <p className="mt-2 max-w-2xl text-base text-slate-500">
            Deficiencies generated from inspections or incidents — responsible person, due date, status,
            verification evidence, and a link to a ROUT work order when service work is required.
          </p>
        </div>
        {admin && (
          <div className="flex gap-2">
            <button onClick={seed} data-testid="ops-ca-seed" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50">
              <Sparkles size={15} /> Seed from Deficiencies
            </button>
            <button onClick={() => setCreating(true)} data-testid="ops-ca-new" className="inline-flex items-center gap-2 rounded-lg bg-[#0E2A47] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#0E2A47]/90">
              <Plus size={15} /> New Corrective Action
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="ops-ca-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search action #, deficiency, responsible, location"
            className="w-72 rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#8FC93A]" />
        </div>
        <select data-testid="ops-ca-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#8FC93A]">
          <option value="all">All Statuses</option>
          <option value="open">Open + In Progress</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600" data-testid="ops-ca-overdue-toggle">
          <input type="checkbox" checked={overdue} onChange={(e) => setOverdue(e.target.checked)} className="h-4 w-4 accent-red-500" />
          Overdue only
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600" data-testid="ops-ca-followup-toggle">
          <input type="checkbox" checked={followupDue} onChange={(e) => setFollowupDue(e.target.checked)} className="h-4 w-4 accent-amber-500" />
          Follow-up visit due
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600" data-testid="ops-ca-attention-toggle">
          <input type="checkbox" checked={needsAttention} onChange={(e) => setNeedsAttention(e.target.checked)} className="h-4 w-4 accent-amber-600" />
          Needs owner / due date
        </label>
        <label className={`flex cursor-pointer items-center gap-2 text-sm ${demoMode ? 'text-fuchsia-600 font-semibold' : 'text-slate-600'}`} data-testid="ops-ca-demo-toggle">
          <input type="checkbox" checked={demoMode} onChange={(e) => setDemoMode(e.target.checked)} className="h-4 w-4 accent-fuchsia-500" />
          Demo view
        </label>
        <span className="ml-auto text-sm text-slate-400" data-testid="ops-ca-count">{filtered.length} action(s){demoMode ? ' · DEMO' : ''}</span>
        {canPurge && <PurgeButton count={selected.length} onClick={() => setShowPurge(true)} />}
      </div>

      {err && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">{err}</div>}

      {!err && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="ops-ca-table">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                {canPurge && <th className="px-4 py-3 w-8"></th>}
                <th className="px-4 py-3">Action #</th>
                <th className="px-4 py-3">Deficiency</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Responsible</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">ROUT WO</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" data-testid={`ops-ca-row-${r.id}`} onClick={() => admin && setEditing(r)}>
                  {canPurge && <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSel(r.id)} className="h-4 w-4 accent-red-600" data-testid={`ops-ca-select-${r.id}`} /></td>}
                  <td className="px-4 py-2.5 font-mono text-xs text-[#0E2A47]">{r.action_number}</td>
                  <td className="max-w-[260px] px-4 py-2.5"><div className="truncate font-medium text-[#0E2A47]">{r.summary}</div>{r.location_name && <div className="truncate text-xs text-slate-400">{r.location_name}</div>}</td>
                  <td className="px-4 py-2.5 text-slate-600 capitalize">{r.source_type}</td>
                  <td className="px-4 py-2.5"><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.Medium}`}>{r.priority}</span></td>
                  <td className="px-4 py-2.5"><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.status] || STATUS_STYLE.Open}`}>{r.status}</span></td>
                  <td className="px-4 py-2.5 text-slate-600">{r.responsible_person || <span className="text-slate-300">Unassigned</span>}</td>
                  <td className="px-4 py-2.5">
                    <span className={r.is_overdue ? 'inline-flex items-center gap-1 font-semibold text-red-600' : 'text-slate-600'}>
                      {r.is_overdue && <AlertTriangle size={12} />}{fmtDate(r.due_date)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">{r.linked_work_order_number ? <span className="inline-flex items-center gap-1 font-mono text-xs text-[#5FA718]"><Link2 size={12} />{r.linked_work_order_number}</span> : <span className="text-slate-300">—</span>}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={canPurge ? 9 : 8} className="px-4 py-12 text-center text-slate-400">
                  <Wrench size={28} className="mx-auto mb-2 text-slate-300" />
                  No corrective actions yet. {admin && 'Use “Seed from Deficiencies” to import existing inspection/incident findings.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(editing || creating) && (
        <CorrectiveActionDialog
          token={token} record={editing} isNew={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
      {showPurge && <PurgeReviewDialog entityType="corrective_action" ids={selected} onClose={() => setShowPurge(false)} onDone={() => { setShowPurge(false); load(); }} />}
    </div>
  );
};

const CorrectiveActionDialog = ({ token, record, isNew, onClose, onSaved }) => {
  const [f, setF] = useState(() => ({
    summary: record?.summary || '',
    priority: record?.priority || 'Medium',
    status: record?.status || 'Open',
    responsible_person: record?.responsible_person || '',
    due_date: record?.due_date ? String(record.due_date).slice(0, 10) : '',
    required_action: record?.required_action || '',
    verification_notes: record?.verification_notes || '',
    linked_work_order_number: record?.linked_work_order_number || '',
    notes: record?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.summary.trim()) { toast.error('A deficiency summary is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...f, due_date: f.due_date || null };
      if (isNew) {
        await axios.post(`${API}/operations/corrective-actions`, payload, auth(token));
        toast.success('Corrective action created.');
      } else {
        await axios.patch(`${API}/operations/corrective-actions/${record.id}`, payload, auth(token));
        toast.success('Corrective action updated.');
      }
      onSaved();
    } catch { toast.error('Save failed.'); } finally { setSaving(false); }
  };

  const Field = ({ label, children }) => (
    <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>{children}</div>
  );
  const inputCls = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#8FC93A]';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="ops-ca-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Corrective Action' : `Corrective Action ${record?.action_number || ''}`}</DialogTitle>
          <DialogDescription>Deficiency details, responsible owner, due date, verification evidence and an optional ROUT work-order link.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
          <Field label="Deficiency Summary"><input data-testid="ops-ca-f-summary" value={f.summary} onChange={(e) => set('summary', e.target.value)} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority"><select data-testid="ops-ca-f-priority" value={f.priority} onChange={(e) => set('priority', e.target.value)} className={inputCls}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></Field>
            <Field label="Status"><select data-testid="ops-ca-f-status" value={f.status} onChange={(e) => set('status', e.target.value)} className={inputCls}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Responsible Person"><input data-testid="ops-ca-f-responsible" value={f.responsible_person} onChange={(e) => set('responsible_person', e.target.value)} className={inputCls} /></Field>
            <Field label="Due Date"><input type="date" data-testid="ops-ca-f-due" value={f.due_date} onChange={(e) => set('due_date', e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Required Action"><textarea data-testid="ops-ca-f-action" value={f.required_action} onChange={(e) => set('required_action', e.target.value)} rows={2} className={inputCls} /></Field>
          <Field label="Verification / Completion Evidence"><textarea data-testid="ops-ca-f-verify" value={f.verification_notes} onChange={(e) => set('verification_notes', e.target.value)} rows={2} className={inputCls} /></Field>
          <Field label="Linked ROUT Work Order #"><input data-testid="ops-ca-f-wo" value={f.linked_work_order_number} onChange={(e) => set('linked_work_order_number', e.target.value)} placeholder="e.g. WO-2026-0142" className={inputCls} /></Field>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="ops-ca-save" className="rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0E2A47]/90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OperationsCorrectiveActions;
